import { useRef } from 'react';
import { createPortal } from 'react-dom';
import useModal from '../../../hooks/use-close-modal.hook';
import Icon from '../../forms/icon.component';

interface EmailPreviewModalProps {
  show: boolean;
  onRequestClose: () => void;
  payload: any;
  varsEmail: any;
  examplesCampaigns: any;
  subject: string;
}

function EmailPreviewModal({
  show,
  onRequestClose,
  payload,
  varsEmail,
  examplesCampaigns,
  subject
}: EmailPreviewModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);

  const replaceVariables = (text: string): string => {
    if (!text) return '';

    return text.replace(/{{([^{}]+)}}/g, (match, key) => {
      const cleanKey = `{{${key.trim()}}}`;

      if (varsEmail) {
        for (const section of Object.values(varsEmail)) {
          if (section && typeof section === 'object') {
            for (const [varKey, columnName] of Object.entries(section)) {
              if (varKey === cleanKey && columnName) {
                const example = Array.isArray(examplesCampaigns)
                  ? examplesCampaigns.find((v: any) => v?.header === columnName)
                  : null;
                if (example) {
                  return replaceVariables(example.value);
                }
              }
            }
          }
        }
      }

      const variable = Array.isArray(examplesCampaigns)
        ? examplesCampaigns.find((v: any) => v?.header === key.trim())
        : null;
      if (variable) {
        return replaceVariables(variable.value);
      }

      return match;
    });
  };

  const getReplacedSubject = () => {
    return replaceVariables(subject || '');
  };

  const getReplacedBody = () => {
    const bodyContent = payload?.html || payload?.body || '';
    return replaceVariables(bodyContent);
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!payload) return null;

  return createPortal(
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static" role="dialog">
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center">
              <Icon icon="ki-sms" size="fs-2" type="primary" className="me-3" />
              Vista Previa del Email - {payload?.name || 'Plantilla'}
            </h5>
            <div className="btn btn-sm btn-icon btn-active-light-primary" onClick={onRequestClose}>
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <div className="modal-body p-0">
            <div className="row g-0">
              <div className="col-12 p-6">
                <div className="email-preview-modal-container">
                  {/* Header del email */}
                  <div className="email-header-modal">
                    <div className="email-header-row-modal">
                      <span className="email-label-modal">De:</span>
                      <span className="email-value-modal">
                        {payload?.fromName || 'Campaña Email'} &lt;
                        {payload?.fromEmail || 'noreply@empresa.com'}&gt;
                      </span>
                    </div>
                    <div className="email-header-row-modal">
                      <span className="email-label-modal">Para:</span>
                      <span className="email-value-modal">
                        {examplesCampaigns?.find((v: any) =>
                          v?.header?.toLowerCase()?.includes('email')
                        )?.value || 'cliente@ejemplo.com'}
                      </span>
                    </div>
                    <div className="email-header-row-modal">
                      <span className="email-label-modal">Asunto:</span>
                      <span className="email-value-modal email-subject-modal">
                        {getReplacedSubject()}
                      </span>
                    </div>
                    <div className="email-header-row-modal">
                      <span className="email-label-modal">Fecha:</span>
                      <span className="email-value-modal">{getCurrentDateTime()}</span>
                    </div>
                  </div>

                  {/* Cuerpo del email */}
                  <div className="email-body-modal">
                    {payload?.html ? (
                      <div
                        className="email-html-content-modal"
                        dangerouslySetInnerHTML={{ __html: getReplacedBody() }}
                      />
                    ) : (
                      <div className="email-text-content-modal">
                        {getReplacedBody()
                          .split('\n')
                          .map((line: string, index: number) => (
                            <p key={index}>{line}</p>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onRequestClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default EmailPreviewModal;
